/**
 * Question API service.
 *
 * Centralizes all question-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module questionApi
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/apiUtils';
import type { PaginatedResponse } from '@/types/pagination';

export interface QuestionFilters {
  level?: string;
  section?: string;
  examType?: string;
  module?: string;
  search?: string;
  isActive?: boolean;
}

export interface PaginationOptions {
  page?: number;
  limit?: number;
}

/** Single exam question row from the paginated questions list endpoint. */
export interface ExamQuestionListItem {
  id: string;
  type: string;
  text: string;
  options: string[] | null;
  expectedAnswer: string;
  explanation: string;
  taskType?: string | null;
  level: string;
  skill: string;
  section: number;
  examTypes: string[];
  isActive: boolean;
  contextId: string | null;
  createdAt: string;
  updatedAt?: string;
  generationModel?: string | null;
  context?: {
    generationModel?: string | null;
    [key: string]: unknown;
  } | null;
}

function buildQuestionQueryParams(
  filters: QuestionFilters,
  pagination: PaginationOptions,
): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.level && filters.level !== 'all') {
    params.append('level', filters.level);
  }
  if (filters.section && filters.section !== 'all') {
    params.append('section', filters.section);
  }
  if (filters.examType && filters.examType !== 'all') {
    params.append('examType', filters.examType);
  }
  if (filters.module && filters.module !== 'all') {
    params.append('module', filters.module);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.isActive !== undefined) {
    params.append('isActive', filters.isActive.toString());
  }
  if (pagination.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination.limit) {
    params.append('limit', pagination.limit.toString());
  }

  return params;
}

/**
 * Get all questions with optional filters and pagination
 *
 * @param filters - Optional filters for level, section, examType, module, search
 * @param pagination - Optional pagination options
 * @returns Promise resolving to paginated questions response
 * @throws Error if API request fails
 */
export const getAllQuestions = async (
  filters: QuestionFilters = {},
  pagination: PaginationOptions = {},
): Promise<PaginatedResponse<ExamQuestionListItem>> => {
  const params = buildQuestionQueryParams(filters, pagination);
  const result = await apiGet<PaginatedResponse<ExamQuestionListItem>>(
    `/questions/?${params}`,
    {
      errorMessage: 'Failed to fetch questions',
      context: 'getAllQuestions',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to fetch questions');
  }
  return result;
};

/**
 * Get a single question by ID
 *
 * @param id - Question ID
 * @returns Promise resolving to question object
 * @throws Error if API request fails
 */
export const getQuestionById = async (id: string): Promise<any> => {
  const result = await apiGet<any>(`/questions/${id}`, {
    errorMessage: 'Failed to fetch question',
    context: 'getQuestionById',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch question');
  }
  return result;
};

/**
 * Create a new question
 *
 * @param questionData - Question data to create
 * @returns Promise resolving to created question object
 * @throws Error if API request fails
 */
export const createQuestion = async (questionData: any): Promise<any> => {
  const result = await apiPost<any>('/questions/', questionData, {
    errorMessage: 'Failed to create question',
    context: 'createQuestion',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to create question');
  }
  return result;
};

/**
 * Create multiple questions in batch
 *
 * @param contextId - Context ID for the questions
 * @param questions - Array of question data
 * @returns Promise resolving to batch creation result
 * @throws Error if API request fails
 */
export const createQuestionsBatch = async (
  contextId: string,
  questions: any[],
): Promise<any> => {
  const result = await apiPost<any>(
    '/questions/batch',
    { contextId, questions },
    {
      errorMessage: 'Failed to create questions in batch',
      context: 'createQuestionsBatch',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to create questions in batch');
  }
  return result;
};

/**
 * Update an existing question
 *
 * @param id - Question ID to update
 * @param questionData - Updated question data
 * @returns Promise resolving to updated question object
 * @throws Error if API request fails
 */
export const updateQuestion = async (
  id: string,
  questionData: any,
): Promise<any> => {
  const result = await apiPut<any>(`/questions/${id}`, questionData, {
    errorMessage: 'Failed to update question',
    context: 'updateQuestion',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to update question');
  }
  return result;
};

/**
 * Delete a question by ID
 *
 * @param id - Question ID to delete
 * @returns Promise resolving on successful deletion
 * @throws Error if API request fails
 */
export const deleteQuestion = async (id: string): Promise<void> => {
  await apiDelete(`/questions/${id}`, {
    errorMessage: 'Failed to delete question',
    context: 'deleteQuestion',
    throwOnError: true,
  });
};

/**
 * Delete multiple questions in bulk
 *
 * @param ids - Array of question IDs to delete
 * @returns Promise resolving on successful deletion
 * @throws Error if API request fails
 */
export const deleteBulkQuestions = async (ids: string[]): Promise<void> => {
  await apiDelete('/questions/bulk', {
    body: JSON.stringify({ ids }),
    errorMessage: 'Failed to delete questions',
    context: 'deleteBulkQuestions',
    throwOnError: true,
  });
};

/**
 * Get questions by context ID
 *
 * @param contextId - Context ID to filter by
 * @param excludeQuestionId - Optional question ID to exclude from results
 * @returns Promise resolving to array of questions
 * @throws Error if API request fails
 */
export const getQuestionsByContextId = async (
  contextId: string,
  excludeQuestionId?: string,
): Promise<any[]> => {
  const params = new URLSearchParams();
  params.append('contextId', contextId);
  params.append('limit', '100');

  const data = await apiGet<PaginatedResponse<ExamQuestionListItem>>(
    `/questions/?${params}`,
    {
      errorMessage: 'Failed to fetch questions by context',
      context: 'getQuestionsByContextId',
      throwOnError: true,
    },
  );

  let questions = data?.items || [];

  if (excludeQuestionId) {
    questions = questions.filter((q) => q.id !== excludeQuestionId);
  }

  return questions;
};
